import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { MonoLabel } from '../components/Editorial';
import {
  parseRecruitParams, defaultRecruitMode, RECRUIT_MODES, DEFAULT_MODE,
} from '../lib/recruitParams';
import MePanel from '../components/recruit/MePanel';
import MatchesPanel from '../components/recruit/MatchesPanel';
import ActivityRail from '../components/recruit/rail/ActivityRail';
import '../components/recruit/recruit.css';

// Inline 14x14 magnifier glyph for the "search any school" entry field.
const SearchGlyph = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

/**
 * The athlete Recruit surface: one route, two modes.
 *
 * Replaces the old /offers + /following + /matches split, matching the apps'
 * single Recruit tab (OffersView.swift / OffersScreen.kt). Schools is one
 * scroll — search, matches, then the unified activity rail — and this shell
 * owns URL state and page chrome and nothing else.
 *
 * Specs: docs/superpowers/specs/2026-08-10-web-recruit-ia-design.md (F2)
 *        docs/superpowers/specs/2026-08-10-web-recruit-activity-rail-design.md (F3)
 */

const MODE_LABELS = { me: 'Me', schools: 'Schools' };

export default function RecruitPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { API_URL } = useAuth();
  // `tab` is no longer read here — F3 replaced the Schools tabs with the rail,
  // and ActivityRail resolves any legacy ?tab= into a filter itself.
  const { mode } = parseRecruitParams(searchParams);

  // With no ?mode= in the URL, an athlete whose record is empty opens on Me —
  // that is the work in front of them. Resolved once per visit and written into
  // the URL, so the athlete's own later choice always wins. An explicit ?mode=
  // short-circuits this entirely and no profile is fetched for it.
  const explicitMode = searchParams.get('mode');
  useEffect(() => {
    if (explicitMode) return undefined;
    let cancelled = false;
    const cfg = { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } };
    // Film is fetched explicitly: /athletes/profile carries measurables but no
    // film signal, and inferring one from a field that isn't there would send
    // athletes who have uploaded film to the wrong mode.
    Promise.all([
      axios.get(`${API_URL}/athletes/profile`, cfg),
      axios.get(`${API_URL}/videos/me`, cfg).catch(() => ({ data: [] })),
    ]).then(([profileRes, videoRes]) => {
      if (cancelled) return;
      const d = profileRes.data || {};
      const hasMeasurables = (d.measurables || [])
        .some((m) => m.value != null && m.value !== '');
      const hasFilm = (videoRes.data || []).some((v) => v.video_url);
      const resolved = defaultRecruitMode({ hasMeasurables, hasFilm });
      if (resolved !== DEFAULT_MODE) {
        const next = new URLSearchParams(searchParams);
        next.set('mode', resolved);
        setSearchParams(next, { replace: true });
      }
    }).catch(() => {
      // Leave the athlete on the Schools fallback rather than bouncing them.
    });
    return () => { cancelled = true; };
    // searchParams/setSearchParams are stable enough here; re-running on every
    // param change would re-fetch the profile on each tab click.
  }, [API_URL, explicitMode]);

  // Preserve any params the panels rely on (MatchesPanel reads ?peek=) rather
  // than rebuilding the query string from scratch.
  const setParam = (key, value) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    // replace: flipping mode is not a navigation the athlete should have to
    // unwind one Back press at a time.
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="ed-page">
      <div className="recruit-header">
        <MonoLabel onDark>YOUR RECRUITING</MonoLabel>
        <h1 className="recruit-header__title">Your athletics, your schools</h1>

        <div className="recruit-segmented" role="tablist" aria-label="Recruit mode">
          {RECRUIT_MODES.map((m) => (
            <button
              key={m}
              type="button"
              role="tab"
              aria-selected={mode === m}
              className={`recruit-segmented__option${mode === m ? ' recruit-segmented__option--active' : ''}`}
              onClick={() => setParam('mode', m)}
              data-testid={`recruit_mode_${m}`}
            >
              {MODE_LABELS[m]}
            </button>
          ))}
        </div>
      </div>

      {mode === 'me' && <MePanel />}

      {mode === 'schools' && (
        <>
          {/* Search is a lookup tool rather than a bucket, so it pushes its own
              screen here exactly as it does in the apps. Reuses the entry
              treatment from the old Offers page — a ghost button would be
              invisible on this light surface (see the .ed-btn--ghost note in
              Editorial.css: the default ghost is styled for the dark hero). */}
          <div className="offers-search-entry">
            <div className="offers-search-entry__eyebrow-row">
              <MonoLabel>Search any school</MonoLabel>
            </div>
            <button
              type="button"
              className="offers-search-entry__field"
              onClick={() => navigate('/schools/search')}
              aria-label="Search schools by name"
            >
              <SearchGlyph />
              <span className="offers-search-entry__field-text">Search schools by name</span>
              <span className="offers-search-entry__chevron" aria-hidden="true">&rsaquo;</span>
            </button>
          </div>

          {/* Matches keeps its own section above the rail. Matches aren't dated
              events — folding them into a timeline would sort them
              meaninglessly, which is why the apps keep them separate too. */}
          <MatchesPanel />

          <ActivityRail />
        </>
      )}
    </div>
  );
}
